import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Progress } from "@/components/ui/progress"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { RefreshCw, FolderOpen, Calendar, Users, ChevronLeft, ChevronRight, Clock, AlertCircle, ExternalLink, Kanban as KanbanIcon } from "lucide-react"
import { getStatusColors } from "@/lib/status-colors"
import { getTagColors } from "@/lib/tag-colors"
import { toast } from "@/hooks/use-toast"
import { useProductionManagement } from "@/lib/production-management-context"
import { NotionAvatar } from "@/components/notion-avatar"
import { useSprints } from "@/lib/sprint-context"
import type { SprintTask, Sprint } from "@/lib/types"

interface Project {
  id: string
  name: string
  status?: string
  startDate?: string
  endDate?: string
  taskCount?: string | number
  budget?: string | number
  budgetRealised?: string | number | null
  projectCode?: string
  priority?: string
  gdriveRoom?: string
  description?: string
  team?: string[]
  progress?: number
  [key: string]: any
}

// Week calculation helpers
function getWeekNumber(date: Date): { week: number; year: number } {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  const dayNum = d.getUTCDay() || 7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum)
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
  const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7)
  return { week: weekNo, year: d.getUTCFullYear() }
}

function getWeekDateRange(weekNumber: number, year: number): { start: Date; end: Date } {
  const simple = new Date(year, 0, 1 + (weekNumber - 1) * 7)
  const dow = simple.getDay()
  const ISOweekStart = simple
  if (dow <= 4) {
    ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1)
  } else {
    ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay())
  }
  const start = new Date(ISOweekStart)
  const end = new Date(start)
  end.setDate(start.getDate() + 6)
  return { start, end }
}

function formatWeekLabel(week: number, year: number): string {
  return `W${week.toString().padStart(2, '0')}.${year.toString().slice(-2)}`
}

function calculateSprintName(startDate?: string, endDate?: string): string {
  if (!startDate || !endDate) return 'Sprint'
  
  const start = new Date(startDate)
  const end = new Date(endDate)
  const { week: startWeek, year: startYear } = getWeekNumber(start)
  const { week: endWeek, year: endYear } = getWeekNumber(end)
  
  if (startWeek === endWeek && startYear === endYear) {
    return formatWeekLabel(startWeek, startYear)
  }
  
  return `${formatWeekLabel(startWeek, startYear)} - ${formatWeekLabel(endWeek, endYear)}`
}

function formatDateRange(start: Date, end: Date): string {
  const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  return `${startStr} - ${endStr}`
}

function projectIntersectsWeek(project: Project, weekStart: Date, weekEnd: Date, sprintTasks: SprintTask[], sprints: Sprint[]): boolean {
  // Find all tasks for this project
  const projectTasks = sprintTasks.filter(task => task.projectId === project.id)
  
  if (projectTasks.length === 0) return false
  
  // Check if any task's sprint intersects with this week
  return projectTasks.some(task => {
    return task.sprints.some(sprintId => {
      const sprint = sprints.find(s => s.id === sprintId)
      if (!sprint || !sprint.startDate || !sprint.endDate) return false
      
      const sprintStart = new Date(sprint.startDate)
      const sprintEnd = new Date(sprint.endDate)
      
      return sprintStart <= weekEnd && sprintEnd >= weekStart
    })
  })
}

export default function ProductionManagementPage() {
  const navigate = useNavigate()
  const { projects, loadingProjects, refreshProjects, resources, getUsersByIds } = useProductionManagement()
  const { sprintTasks, sprints, refreshSprints } = useSprints()
  const [currentSprintOffset, setCurrentSprintOffset] = useState(0)
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [projectViewMode, setProjectViewMode] = useState<"timeline" | "kanban">("timeline")
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [currentWeekOffset, setCurrentWeekOffset] = useState(0) // 0 = current week, 1 = next week, etc.
  const [showDoneProjects, setShowDoneProjects] = useState(false)
  const [showMaintenanceProjects, setShowMaintenanceProjects] = useState(true)

  // Helper function to get contributor names from IDs
  const getContributorNames = (contributorIds?: string[]): string => {
    if (!contributorIds || contributorIds.length === 0) return ''
    
    const names = contributorIds
      .map(id => {
        const resource = resources.find(r => r.id === id)
        return resource?.name?.trim() || null
      })
      .filter(Boolean) as string[]
    
    return names.length > 0 ? names.join(', ') : ''
  }

  const loadData = async (force = false) => {
    try {
      if (force) setIsRefreshing(true)
      await refreshProjects(force)
    } catch (err) {
      console.error("Failed to fetch project management data:", err)
      toast({
        title: "Error",
        description: "Failed to load project management data. Please try again.",
      })
    } finally {
      setIsRefreshing(false)
    }
  }

  const loadSprintData = async (force = false) => {
    try {
      if (force) setIsRefreshing(true)
      await refreshSprints(force)
      
      // Set offset to current sprint on initial load
      const currentSprintIndex = sprints.findIndex(s => s.status?.toLowerCase() === 'current')
      if (currentSprintIndex !== -1) {
        setCurrentSprintOffset(currentSprintIndex)
      }
    } catch (err) {
      console.error("Error fetching sprint data:", err)
      toast({
        title: "Error",
        description: "Failed to load sprint data. Please try again.",
      })
    } finally {
      if (force) setIsRefreshing(false)
    }
  }

  useEffect(() => {
    loadData()
    loadSprintData()
  }, [])

  const handleRefresh = () => {
    loadData(true)
    loadSprintData(true)
  }

  if (loadingProjects) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-8 w-64 mb-2" />
            <Skeleton className="h-4 w-96" />
          </div>
          <Skeleton className="h-10 w-28" />
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  // Filter projects based on status and tags
  const filteredProjects = projects.filter(project => {
    const status = project.status?.toLowerCase() || ''
    
    // Filter out Done projects unless showDoneProjects is true
    if (!showDoneProjects && (status.includes('done') || status.includes('completed') || status.includes('finished'))) {
      return false
    }
    
    // Filter out Maintenance projects unless showMaintenanceProjects is true
    if (!showMaintenanceProjects && status.includes('maintenance')) {
      return false
    }
    
    // If tags are selected, filter by project tasks
    if (selectedTags.length > 0) {
      const projectTasks = sprintTasks.filter(task => task.projectId === project.id)
      
      // Include projects without tasks (new projects)
      if (projectTasks.length === 0) return true
      
      // Check if any task has a selected tag
      return projectTasks.some(task => 
        Array.isArray(task.tags) && 
        task.tags.some(tag => selectedTags.includes(tag))
      )
    }
    
    return true
  })

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Production Management</h1>
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              Overview of all your projects and their current status
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => setShowDoneProjects(!showDoneProjects)}
              variant={showDoneProjects ? "default" : "outline"}
              size="sm"
            >
              {showDoneProjects ? "Hide" : "Show"} Done
            </Button>
            <Button 
              onClick={() => setShowMaintenanceProjects(!showMaintenanceProjects)}
              variant={showMaintenanceProjects ? "outline" : "default"}
              size="sm"
            >
              {showMaintenanceProjects ? "Hide" : "Show"} Maintenance
            </Button>
            <Button onClick={handleRefresh} disabled={isRefreshing} variant="outline">
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      {filteredProjects.length > 0 && (
        <div className="grid gap-4 md:grid-cols-5">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Projects</CardTitle>
              <FolderOpen className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{filteredProjects.length}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">To-do</CardTitle>
              <FolderOpen className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {filteredProjects.filter(p => {
                  const status = p.status?.toLowerCase()
                  return status?.includes("planning") || status?.includes("backlog")
                }).length}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">In Progress</CardTitle>
              <FolderOpen className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {filteredProjects.filter(p => {
                  const status = p.status?.toLowerCase()
                  return status?.includes("in progress") || status?.includes("maintenance") || status?.includes("active")
                }).length}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Completed</CardTitle>
              <FolderOpen className="h-4 w-4 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {filteredProjects.filter(p => {
                  const status = p.status?.toLowerCase()
                  return status?.includes("done") || status?.includes("cancelled")
                }).length}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">On Hold</CardTitle>
              <FolderOpen className="h-4 w-4 text-yellow-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {filteredProjects.filter(p => {
                  const status = p.status?.toLowerCase()
                  return status?.includes("paused")
                }).length}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content with Tabs */}
      <Tabs defaultValue="projects" className="space-y-6 mt-8">
        <TabsList>
          <TabsTrigger value="projects">Projects Timeline</TabsTrigger>
          <TabsTrigger value="sprints">Sprint Board ({sprintTasks.length})</TabsTrigger>
        </TabsList>

        {/* Projects Timeline Tab */}
        <TabsContent value="projects" className="space-y-6">
          {/* View Switcher */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Project Portfolio</h3>
              <p className="text-sm text-muted-foreground">
                {filteredProjects.length} projects
              </p>
            </div>
            
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              <Button
                variant={projectViewMode === "timeline" ? "default" : "ghost"}
                size="sm"
                onClick={() => setProjectViewMode("timeline")}
                className="h-8 px-3"
              >
                <Calendar className="h-4 w-4 mr-1" />
                Timeline
              </Button>
              <Button
                variant={projectViewMode === "kanban" ? "default" : "ghost"}
                size="sm"
                onClick={() => setProjectViewMode("kanban")}
                className="h-8 px-3"
              >
                <KanbanIcon className="h-4 w-4 mr-1" />
                Kanban
              </Button>
            </div>
          </div>

      {/* Timeline View */}
      {projectViewMode === "timeline" && projects.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Timeline View</CardTitle>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setCurrentWeekOffset(currentWeekOffset - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setCurrentWeekOffset(0)}
                >
                  Current
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setCurrentWeekOffset(currentWeekOffset + 1)}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              {[0, 1, 2].map((weekIndex) => {
                const today = new Date()
                const targetDate = new Date(today)
                targetDate.setDate(today.getDate() + (currentWeekOffset + weekIndex) * 7)
                
                const { week, year } = getWeekNumber(targetDate)
                const { start, end } = getWeekDateRange(week, year)
                const weekLabel = formatWeekLabel(week, year)
                const dateRange = formatDateRange(start, end)
                
                const weekProjects = filteredProjects.filter(project => 
                  projectIntersectsWeek(project, start, end, sprintTasks, sprints)
                )
                
                const isCurrentWeek = currentWeekOffset + weekIndex === 0
                
                return (
                  <div 
                    key={weekIndex}
                    className={`border rounded-lg p-4 ${isCurrentWeek ? 'border-primary bg-primary/5' : 'border-border'}`}
                  >
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-lg">{weekLabel}</h3>
                        {isCurrentWeek && (
                          <Badge variant="default" className="text-xs">Current</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{dateRange}</p>
                    </div>
                    
                    <div className="space-y-2">
                      {weekProjects.length === 0 ? (
                        <p className="text-sm text-muted-foreground italic">No projects this week</p>
                      ) : (
                        <>
                          <p className="text-sm font-medium mb-2">{weekProjects.length} project{weekProjects.length !== 1 ? 's' : ''}</p>
                          <div className="space-y-2 max-h-96 overflow-y-auto">
                            {weekProjects.map(project => {
                              // Calculate hours by tag for this project
                              const projectTasks = sprintTasks.filter(task => task.projectId === project.id)
                              const projectHoursByTag: Record<string, { estimated: number, realised: number }> = {}
                              
                              projectTasks.forEach(task => {
                                if (task.tags && task.tags.length > 0) {
                                  task.tags.forEach(tag => {
                                    if (!projectHoursByTag[tag]) {
                                      projectHoursByTag[tag] = { estimated: 0, realised: 0 }
                                    }
                                    projectHoursByTag[tag].estimated += task.estimatedHours || 0
                                    projectHoursByTag[tag].realised += task.realisedHours || 0
                                  })
                                }
                              })
                              
                              return (
                              <Link 
                                key={project.id} 
                                to={`/production-management/projects/${project.id}`}
                                className="block"
                              >
                                <Card className="hover:shadow-md transition-shadow border-l-4" style={{ borderLeftColor: getStatusColors(project.status || "").borderColorValue }}>
                                  <CardContent className="p-3">
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                      <h4 className="text-sm font-semibold line-clamp-2 flex-1">
                                        {project.name}
                                      </h4>
                                      <Badge className={`text-xs shrink-0 whitespace-nowrap ${getStatusColors(project.status || "").bg} ${getStatusColors(project.status || "").text} ${getStatusColors(project.status || "").border} border`}>
                                        {project.status || "Unknown"}
                                      </Badge>
                                    </div>
                                    {project.projectCode && (
                                      <p className="text-xs text-muted-foreground mb-2">
                                        {project.projectCode}
                                      </p>
                                    )}
                                    
                                    {/* Owner and Collaborators */}
                                    {(() => {
                                      const owners = getUsersByIds(project.owner);
                                      const collaborators = getUsersByIds(project.collaborators);
                                      const owner = owners.length > 0 ? owners[0] : null;
                                      
                                      if (!owner && collaborators.length === 0) return null;
                                      
                                      return (
                                        <div className="flex flex-wrap gap-2 mb-2">
                                          {owner && (
                                            <div className="flex items-center gap-1 text-xs">
                                              <NotionAvatar 
                                                name={owner.name}
                                                profileImage={owner.profileImage}
                                                className="h-4 w-4"
                                              />
                                              <span className="font-medium text-muted-foreground">{owner.name}</span>
                                            </div>
                                          )}
                                          {collaborators.length > 0 && (
                                            <div className="flex items-center gap-1">
                                              {collaborators.slice(0, 3).map((collab) => (
                                                <NotionAvatar 
                                                  key={collab.id}
                                                  name={collab.name}
                                                  profileImage={collab.profileImage}
                                                  className="h-4 w-4"
                                                  alt={collab.name}
                                                />
                                              ))}
                                              {collaborators.length > 3 && (
                                                <span className="text-xs text-muted-foreground">+{collaborators.length - 3}</span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}
                                    
                                    {Object.keys(projectHoursByTag).length > 0 && (
                                      <div className="space-y-1.5">
                                        {Object.entries(projectHoursByTag).map(([tag, hours]) => {
                                          const tagColors = getTagColors(tag);
                                          const estimatedDays = (hours.estimated / 8).toFixed(1)
                                          const realisedDays = (hours.realised / 8).toFixed(1)
                                          const percentage = hours.estimated > 0 ? Math.round((hours.realised / hours.estimated) * 100) : 0
                                          return (
                                            <div key={tag} className="space-y-1">
                                              <div className="flex items-center justify-between">
                                                <Badge 
                                                  className={`text-xs px-2 py-0.5 ${tagColors.bg} ${tagColors.text} ${tagColors.border} border`}
                                                >
                                                  {tag}
                                                </Badge>
                                                <span className="text-xs font-medium">
                                                  {realisedDays}d / {estimatedDays}d
                                                </span>
                                              </div>
                                              <div className="w-full bg-muted rounded-full h-1.5">
                                                <div 
                                                  className="bg-primary rounded-full h-1.5 transition-all"
                                                  style={{ width: `${Math.min(percentage, 100)}%` }}
                                                />
                                              </div>
                                            </div>
                                          )
                                        })}
                                      </div>
                                    )}
                                  </CardContent>
                                </Card>
                              </Link>
                              )
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Kanban View */}
      {projectViewMode === "kanban" && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 min-h-[600px]">
          {[
            {
              id: "to-do",
              title: "To-do",
              statuses: ["Planning", "Backlog"],
              color: "border-blue-200 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/50"
            },
            {
              id: "in-progress",
              title: "In Progress",
              statuses: ["In Progress", "Maintenance", "Paused"],
              color: "border-green-200 bg-green-50 dark:border-green-700 dark:bg-green-900/50"
            },
            {
              id: "complete",
              title: "Complete",
              statuses: ["Done", "Cancelled"],
              color: "border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/50"
            }
          ].map((column) => {
            const columnProjects = filteredProjects
              .filter(p => p.status && column.statuses.some(s => p.status?.toLowerCase().includes(s.toLowerCase())))
              .sort((a, b) => {
                const aIndex = column.statuses.findIndex(s => a.status?.toLowerCase().includes(s.toLowerCase()))
                const bIndex = column.statuses.findIndex(s => b.status?.toLowerCase().includes(s.toLowerCase()))
                return aIndex - bIndex
              })
            
            return (
              <div key={column.id} className={`rounded-lg border-2 ${column.color} p-4`}>
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-lg">{column.title}</h3>
                    <Badge variant="secondary" className="text-xs">
                      {columnProjects.length}
                    </Badge>
                  </div>
                  <div className="w-full h-1 bg-current opacity-20 rounded-full"></div>
                </div>
                
                <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                  {columnProjects.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
                        <FolderOpen className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <p className="text-sm text-muted-foreground">No projects in {column.title.toLowerCase()}</p>
                    </div>
                  ) : (
                    columnProjects.map(project => {
                      // Calculate hours by tag for this project
                      const projectTasks = sprintTasks.filter(task => task.projectId === project.id)
                      const projectHoursByTag: Record<string, { estimated: number, realised: number }> = {}
                      
                      projectTasks.forEach(task => {
                        if (task.tags && task.tags.length > 0) {
                          task.tags.forEach(tag => {
                            if (!projectHoursByTag[tag]) {
                              projectHoursByTag[tag] = { estimated: 0, realised: 0 }
                            }
                            projectHoursByTag[tag].estimated += task.estimatedHours || 0
                            projectHoursByTag[tag].realised += task.realisedHours || 0
                          })
                        }
                      })
                      
                      return (
                        <Link key={project.id} to={`/production-management/projects/${project.id}`}>
                          <Card className="cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] border-l-4" style={{ borderLeftColor: getStatusColors(project.status || "").borderColorValue }}>
                            <CardHeader className="pb-2">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
                                  <CardTitle className="text-sm font-medium leading-tight line-clamp-2">
                                    {project.name}
                                  </CardTitle>
                                </div>
                                <Badge className={`text-xs shrink-0 ${getStatusColors(project.status || "").bg} ${getStatusColors(project.status || "").text} ${getStatusColors(project.status || "").border} border`}>
                                  {project.status || "Unknown"}
                                </Badge>
                              </div>
                            </CardHeader>
                            
                            <CardContent className="pt-0">
                              <div className="space-y-2">
                                {project.projectCode && (
                                  <p className="text-xs text-muted-foreground">
                                    {project.projectCode}
                                  </p>
                                )}
                                
                                {/* Owner and Collaborators */}
                                {(() => {
                                  const owners = getUsersByIds(project.owner);
                                  const collaborators = getUsersByIds(project.collaborators);
                                  const owner = owners.length > 0 ? owners[0] : null;
                                  
                                  if (!owner && collaborators.length === 0) return null;
                                  
                                  return (
                                    <div className="flex flex-wrap gap-2">
                                      {owner && (
                                        <div className="flex items-center gap-1 text-xs">
                                          <NotionAvatar 
                                            name={owner.name}
                                            profileImage={owner.profileImage}
                                            className="h-4 w-4"
                                          />
                                          <span className="font-medium text-muted-foreground">{owner.name}</span>
                                        </div>
                                      )}
                                      {collaborators.length > 0 && (
                                        <div className="flex items-center gap-1">
                                          {collaborators.slice(0, 3).map((collab) => (
                                            <NotionAvatar 
                                              key={collab.id}
                                              name={collab.name}
                                              profileImage={collab.profileImage}
                                              className="h-4 w-4"
                                              alt={collab.name}
                                            />
                                          ))}
                                          {collaborators.length > 3 && (
                                            <span className="text-xs text-muted-foreground">+{collaborators.length - 3}</span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                                
                                {Object.keys(projectHoursByTag).length > 0 && (
                                  <div className="space-y-1">
                                    {Object.entries(projectHoursByTag).map(([tag, hours]) => {
                                      const estimatedDays = (hours.estimated / 8).toFixed(1)
                                      const realisedDays = (hours.realised / 8).toFixed(1)
                                      return (
                                        <div key={tag} className="text-xs flex items-center gap-1.5">
                                          <Badge className={`${getTagColors(tag).bg} ${getTagColors(tag).text} ${getTagColors(tag).border} border text-xs px-1 py-0`}>{tag}</Badge>
                                          <span className="text-muted-foreground">
                                            {hours.realised.toFixed(1)}h/{hours.estimated.toFixed(1)}h ({realisedDays}d/{estimatedDays}d)
                                          </span>
                                        </div>
                                      )
                                    })}
                                  </div>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        </Link>
                      )
                    })
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Empty state when no projects */}
      {filteredProjects.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FolderOpen className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium text-muted-foreground">No projects found</p>
            <p className="text-sm text-muted-foreground">Projects will appear here once they are available</p>
          </CardContent>
        </Card>
      )}
        </TabsContent>

        {/* Sprint Board Tab */}
        <TabsContent value="sprints" className="space-y-6">
          {sprints.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium text-muted-foreground">No sprints available</p>
                <p className="text-sm text-muted-foreground">Sprint data will appear here once configured</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Sprint Timeline</CardTitle>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setCurrentSprintOffset(currentSprintOffset - 1)}
                      disabled={currentSprintOffset <= 0}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        const currentSprintIndex = sprints.findIndex(s => s.status?.toLowerCase() === 'current')
                        if (currentSprintIndex !== -1) {
                          setCurrentSprintOffset(Math.max(0, currentSprintIndex - 1))
                        } else {
                          setCurrentSprintOffset(0)
                        }
                      }}
                    >
                      Current
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setCurrentSprintOffset(currentSprintOffset + 1)}
                      disabled={currentSprintOffset >= sprints.length - 3}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Tag Filters */}
                {(() => {
                  const allTags = Array.from(new Set(sprintTasks.flatMap(task => task.tags || [])))
                  return allTags.length > 0 ? (
                    <div className="mb-4 pb-4 border-b">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-muted-foreground">Filter by tags:</span>
                        {allTags.map(tag => {
                          const isSelected = selectedTags.includes(tag)
                          return (
                            <Badge
                              key={tag}
                              variant={isSelected ? "default" : "outline"}
                              className="cursor-pointer"
                              onClick={() => {
                                setSelectedTags(prev => 
                                  isSelected 
                                    ? prev.filter(t => t !== tag)
                                    : [...prev, tag]
                                )
                              }}
                            >
                              {tag}
                            </Badge>
                          )
                        })}
                        {selectedTags.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedTags([])}
                            className="h-6 text-xs"
                          >
                            Clear filters
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : null
                })()}
                <div className="grid gap-4 md:grid-cols-3">
                  {sprints.slice(currentSprintOffset, currentSprintOffset + 3).map((sprint) => {
                    const isCurrentSprint = sprint.status?.toLowerCase() === 'active' || sprint.status?.toLowerCase() === 'in progress' || sprint.status?.toLowerCase() === 'current'
                    const sprintName = calculateSprintName(sprint.startDate, sprint.endDate)
                    
                    // Get tasks for this sprint, filtered by selected tags
                    const sprintTaskList = sprintTasks.filter(task => {
                      if (!task.sprints.includes(sprint.id)) return false
                      if (selectedTags.length === 0) return true
                      return task.tags?.some(tag => selectedTags.includes(tag))
                    })
                    
                    // Sort tasks by status: Backlog, In Progress, Quality Check, Done, Archived
                    const statusOrder = ['backlog', 'in progress', 'quality check', 'done', 'archived']
                    const sortedTaskList = [...sprintTaskList].sort((a, b) => {
                      const statusA = a.status?.toLowerCase() || ''
                      const statusB = b.status?.toLowerCase() || ''
                      const indexA = statusOrder.findIndex(s => statusA.includes(s))
                      const indexB = statusOrder.findIndex(s => statusB.includes(s))
                      const orderA = indexA === -1 ? 999 : indexA
                      const orderB = indexB === -1 ? 999 : indexB
                      return orderA - orderB
                    })
                    
                    // Calculate hours by tag
                    const hoursByTag: Record<string, { estimated: number, realised: number }> = {}
                    sprintTaskList.forEach(task => {
                      if (task.tags && task.tags.length > 0) {
                        task.tags.forEach(tag => {
                          if (!hoursByTag[tag]) {
                            hoursByTag[tag] = { estimated: 0, realised: 0 }
                          }
                          hoursByTag[tag].estimated += task.estimatedHours || 0
                          hoursByTag[tag].realised += task.realisedHours || 0
                        })
                      }
                    })
                    
                    const completedTasks = sprintTaskList.filter(task => 
                      task.status?.toLowerCase() === 'done' || task.status?.toLowerCase() === 'completed'
                    ).length
                    
                    const totalTasks = sprintTaskList.length
                    const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0
                    
                    return (
                      <div 
                        key={sprint.id}
                        className={`border rounded-lg p-4 ${isCurrentSprint ? 'border-primary bg-primary/5' : 'border-border'}`}
                      >
                        <div className="mb-3">
                          <div className="flex items-center justify-between mb-1">
                            <h3 className="font-semibold text-lg">{sprintName}</h3>
                            {isCurrentSprint && (
                              <Badge variant="default" className="text-xs">Active</Badge>
                            )}
                            {sprint.status && !isCurrentSprint && (
                              <Badge variant="outline" className="text-xs">{sprint.status}</Badge>
                            )}
                          </div>
                          {(sprint.startDate || sprint.endDate) && (
                            <>
                              <p className="text-xs text-muted-foreground">
                                {sprint.startDate && new Date(sprint.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                {sprint.startDate && sprint.endDate && ' - '}
                                {sprint.endDate && new Date(sprint.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </p>
                              {Object.keys(hoursByTag).length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {Object.entries(hoursByTag).map(([tag, hours]) => {
                                    const estimatedDays = (hours.estimated / 8).toFixed(1)
                                    const realisedDays = (hours.realised / 8).toFixed(1)
                                    return (
                                      <div key={tag} className="text-xs flex items-center gap-2">
                                        <Badge variant="outline" className="text-xs px-1.5 py-0">{tag}</Badge>
                                        <span className="text-muted-foreground">
                                          {hours.realised.toFixed(1)}h / {hours.estimated.toFixed(1)}h ({realisedDays}d / {estimatedDays}d)
                                        </span>
                                      </div>
                                    )
                                  })}
                                </div>
                              )}
                            </>
                          )}
                          {sprint.goal && (
                            <p className="text-xs text-muted-foreground mt-2 italic">{sprint.goal}</p>
                          )}
                        </div>
                        
                        {/* Sprint Progress */}
                        {totalTasks > 0 && (
                          <div className="space-y-2 mb-3">
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Progress</span>
                              <span className="font-medium">{completedTasks}/{totalTasks} tasks</span>
                            </div>
                            <Progress value={progress} />
                          </div>
                        )}
                        
                        {/* Sprint Tasks */}
                        <div className="space-y-2">
                          {sprintTaskList.length === 0 ? (
                            <p className="text-sm text-muted-foreground italic">No tasks in this sprint</p>
                          ) : (
                            <div className="space-y-2 max-h-96 overflow-y-auto">
                              {sortedTaskList.map(task => {
                                const isOverdue = task.due && task.due < new Date() && 
                                  task.status?.toLowerCase() !== 'done' && 
                                  task.status?.toLowerCase() !== 'completed'
                                
                                return (
                                  <div 
                                    key={task.id}
                                    onClick={() => navigate(`/production-management/task/${task.id}`, { state: { task } })}
                                    className="p-3 rounded bg-card border-l-4 border border-border hover:border-primary/50 transition-colors shadow-sm cursor-pointer"
                                    style={{ borderLeftColor: getStatusColors(task.status).borderColorValue }}
                                  >
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                      <p className="text-sm font-medium line-clamp-2 flex-1">
                                        {task.title}
                                      </p>
                                      <Badge className={`text-xs shrink-0 ${getStatusColors(task.status).bg} ${getStatusColors(task.status).text} ${getStatusColors(task.status).border} border`}>
                                        {task.status || "Not Started"}
                                      </Badge>
                                    </div>
                                    
                                    {task.description && (
                                      <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                                        {task.description}
                                      </p>
                                    )}
                                    
                                    <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                                      {task.due && (
                                        <div className={`flex items-center gap-1 ${isOverdue ? "text-red-500 font-medium" : ""}`}>
                                          <Calendar className="h-3 w-3" />
                                          {task.due.toLocaleDateString()}
                                          {isOverdue && " (!)"}
                                        </div>
                                      )}
                                      {task.priority && (
                                        <div className="flex items-center gap-1">
                                          <AlertCircle className="h-3 w-3" />
                                          {task.priority}
                                        </div>
                                      )}
                                      {(task.estimatedHours !== undefined || task.realisedHours !== undefined) && (
                                        <div className="flex items-center gap-1">
                                          <Clock className="h-3 w-3" />
                                          {task.realisedHours !== undefined && task.estimatedHours !== undefined ? (
                                            <span>{task.realisedHours.toFixed(1)}h / {task.estimatedHours}h</span>
                                          ) : task.realisedHours !== undefined ? (
                                            <span>{task.realisedHours.toFixed(1)}h</span>
                                          ) : (
                                            <span>{task.estimatedHours}h</span>
                                          )}
                                        </div>
                                      )}
                                      {task.contributors && task.contributors.length > 0 && (
                                        <div className="flex items-center gap-1">
                                          <Users className="h-3 w-3" />
                                          {getContributorNames(task.contributors) || task.contributors.length}
                                        </div>
                                      )}
                                      {task.url && (
                                        <a
                                          href={task.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-center gap-1 text-blue-500 hover:underline"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <ExternalLink className="h-3 w-3" />
                                          Notion
                                        </a>
                                      )}
                                    </div>
                                    
                                    {task.tags && task.tags.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-2">
                                        {task.tags.map((tag, idx) => (
                                          <Badge key={idx} variant="outline" className="text-xs">
                                            {tag}
                                          </Badge>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
      </div>
    </div>
  )
}
